// CHAT-SWITCH-A: whether the person is at this tab (Slack's active / away). One tracker per page,
// shared by every presence socket: away after 10 minutes without keyboard, mouse or touch input,
// or after the page has been hidden for 5 minutes; active again on the next input (or when the
// page shows again). Listeners start with the first subscriber and stop with the last.

export const IDLE_MS = 10 * 60_000;
export const HIDDEN_MS = 5 * 60_000;

const INPUTS = ["keydown", "pointerdown", "pointermove", "wheel", "touchstart"] as const;

export type ActivityTracker = {
  isActive(): boolean;
  /** Called with the new state on every change; returns the unsubscribe. */
  subscribe(fn: (active: boolean) => void): () => void;
};

export function createActivityTracker(opts: {
  idleMs?: number;
  hiddenMs?: number;
  now?: () => number;
  win?: Window;
  doc?: Document;
}): ActivityTracker {
  const idleMs = opts.idleMs ?? IDLE_MS;
  const hiddenMs = opts.hiddenMs ?? HIDDEN_MS;
  const now = opts.now ?? (() => Date.now());
  const win = opts.win;
  const doc = opts.doc;
  const subscribers = new Set<(active: boolean) => void>();
  let active = true;
  let lastInput = now();
  let hiddenSince: number | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;

  const evaluate = () => {
    const t = now();
    const next = t - lastInput < idleMs && !(hiddenSince !== null && t - hiddenSince >= hiddenMs);
    if (next !== active) {
      active = next;
      for (const fn of [...subscribers]) fn(active);
    }
    schedule();
  };
  // One timer at the next deadline (input only moves `lastInput`; the timer re-checks).
  const schedule = () => {
    if (timer !== null) clearTimeout(timer);
    timer = null;
    if (!active || !subscribers.size) return;
    const t = now();
    let at = lastInput + idleMs;
    if (hiddenSince !== null) at = Math.min(at, hiddenSince + hiddenMs);
    timer = setTimeout(evaluate, Math.max(0, at - t));
  };
  const onInput = () => {
    lastInput = now();
    if (!active) evaluate();
  };
  const onVisibility = () => {
    if (doc?.visibilityState === "hidden") hiddenSince ??= now();
    else {
      hiddenSince = null;
      lastInput = now();
    }
    evaluate();
  };
  const start = () => {
    active = true;
    lastInput = now();
    hiddenSince = doc?.visibilityState === "hidden" ? now() : null;
    for (const e of INPUTS) win?.addEventListener(e, onInput, { passive: true, capture: true });
    win?.addEventListener("focus", onInput);
    doc?.addEventListener("visibilitychange", onVisibility);
  };
  const stop = () => {
    for (const e of INPUTS) win?.removeEventListener(e, onInput, { capture: true });
    win?.removeEventListener("focus", onInput);
    doc?.removeEventListener("visibilitychange", onVisibility);
    if (timer !== null) clearTimeout(timer);
    timer = null;
  };

  return {
    isActive: () => active,
    subscribe(fn) {
      if (!subscribers.size) start();
      subscribers.add(fn);
      schedule();
      return () => {
        subscribers.delete(fn);
        if (!subscribers.size) stop();
      };
    },
  };
}

let shared: ActivityTracker | null = null;

/** The page's one tracker (window and document when there are any). */
export function activity(): ActivityTracker {
  shared ??= createActivityTracker({
    ...(typeof window !== "undefined" ? { win: window } : {}),
    ...(typeof document !== "undefined" ? { doc: document } : {}),
  });
  return shared;
}
